import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Table,
  Button,
  Badge,
  Dropdown,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Space,
  Tag,
  message,
  Avatar,
  Descriptions,
  Drawer,
  Modal,
  Form,
  Tooltip,
  Popover,
  Spin,
  Switch,
  Alert,
} from 'antd';
import { BankOutlined, CloseCircleOutlined, CopyOutlined, DownloadOutlined, EyeInvisibleOutlined, FilterOutlined, InfoCircleOutlined, MoreOutlined, PlusOutlined, SearchOutlined, SwapOutlined, UndoOutlined, WalletOutlined } from '@ant-design/icons';
import { FiRefreshCw, FiShare2 } from 'react-icons/fi';
import dayjs from 'dayjs';

import DashboardPageWrapper from '../../../../components/layout/DashboardPageWrapper';
import PermissionGate from '../../../../components/auth/PermissionGate';
import ExcelExportOverlay from '../../../../components/common/ExcelExportOverlay';
import AppRangePicker from '@/components/common/AppRangePicker';
import orderService from '../../../../services/orderService';
import ifaService from '../../../../services/ifaService';
import productService from '../../../../services/productService';
import { resolvePricing } from '../../../../services/pricingResolveService';
import useRemoteSelectPage from '../../../../hooks/useRemoteSelectPage';
import { useAuth } from '../../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../../config/permissions';
import {
  STATUS_LABEL,
  STATUS_FILTER_OPTIONS,
  resolveOrderStatusColor,
  resolveOrderStatusLabel,
} from './orderStatus';
import {
  canCancelOrder,
  canExcludeOrderFromReports,
  isCancelRemarksRequired,
  canConfirmPayment,
  canEditOrderPartnerPrice,
  canEditOrderQuantity,
  canShowEditOrderAction,
  canTransferShares,
  canUpdateUtr,
  getPendingTransferAllocations,
  getTransferSharesDisabledHint,
  hasAltcasePrice,
} from './orderActionHelpers';
import AltcasePriceCell from './AltcasePriceCell';
import RmPriceCell from './RmPriceCell';
import ShareTransferReconcileDrawer from './ShareTransferReconcileDrawer';
import {
  getInvestorProfilePath,
  getPartnerDetailsPath,
  ProfileLink,
  resolveInvestorUuid,
  resolvePartnerUuid,
} from './orderEntityLinks';

const { TextArea } = Input;

const SELECT_PAGE_SIZE = 50;

function getPartnerUuid(partner) {
  return partner?.id ?? partner?.uuid ?? '';
}

function getProductId(product) {
  return product?.id ?? product?.uuid ?? '';
}

const formatDate = (value) => (value ? dayjs(value).format('DD MMM YYYY') : '—');

const formatRmLabel = (name) =>
  String(name || 'RM')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ') || 'RM';

const formatDateTime = (value) =>
  value ? dayjs(value).format('DD MMM YYYY HH:mm') : '—';

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(n);
};

function FilterField({ label, children }) {
  return (
    <div className="min-w-0">
      <label className="mb-1.5 block text-sm font-medium text-gray-700">{label}</label>
      {children}
    </div>
  );
}

function DisabledActionHintLabel({ title, hint, muted }) {
  const stopMenuEvent = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <div
      className={`flex items-center justify-between gap-2 ${muted ? 'text-gray-400' : ''}`}
      style={{ width: 210, minWidth: 210 }}
    >
      <span className="min-w-0 flex-1">{title}</span>
      {hint ? (
        <Tooltip title={hint} placement="left" getPopupContainer={() => document.body}>
          <span
            className="inline-flex shrink-0 cursor-help text-base text-gray-400 hover:text-blue-500"
            style={{ pointerEvents: 'auto' }}
            onClick={stopMenuEvent}
            onMouseDown={stopMenuEvent}
          >
            <InfoCircleOutlined />
          </span>
        </Tooltip>
      ) : null}
    </div>
  );
}

function formatOffer(row) {
  const po = row.price_offered;
  const dl = row.price_offered_deadline;
  if (po == null && !dl) return '—';
  const pricePart = po != null ? formatCurrency(po) : '';
  const datePart = dl ? formatDate(dl) : '';
  return [pricePart, datePart].filter(Boolean).join(' · ');
}

function orderRowId(row) {
  return row.id || row.uuid;
}

function resolveOrderProductId(row) {
  return row?.product_uuid ?? row?.product_id ?? row?.product?.uuid ?? row?.product?.id ?? '';
}

const EDIT_PRICING_DEBOUNCE_MS = 450;

function UtrCell({ value }) {
  const utr = String(value || '').trim();
  if (!utr) return '—';

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(utr);
      message.success('UTR copied');
    } catch {
      message.error('Could not copy UTR');
    }
  };

  return (
    <Popover
      trigger="hover"
      mouseEnterDelay={0.15}
      content={
        <div className="flex max-w-sm items-start gap-3">
          <span className="break-all text-sm leading-relaxed text-gray-800">{utr}</span>
          <Button
            type="link"
            size="small"
            icon={<CopyOutlined />}
            onClick={handleCopy}
            className="h-auto shrink-0 px-0"
          >
            Copy
          </Button>
        </div>
      }
    >
      <span className="block max-w-[128px] cursor-default truncate tabular-nums">{utr}</span>
    </Popover>
  );
}

function getOrderColumns({
  canManage,
  onAssigned,
  onUpdated,
  canViewAltcasePrice,
  canViewSupplier,
  showReportExclusionColumn,
}) {
  return [
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      sorter: true,
      render: (value) => formatDate(value),
    },
    {
      title: 'Request ID',
      dataIndex: 'order_req_id',
      key: 'order_req_id',
      width: 96,
      render: (value) => value || '—',
    },
    {
      title: 'Order #',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 160,
      sorter: true,
      render: (value) => <span className="font-medium text-gray-800">{value}</span>,
    },
    {
      title: 'Investor',
      key: 'investor',
      width: 280,
      render: (_, row) => {
        const investorUuid = resolveInvestorUuid(row);
        const partnerUuid = resolvePartnerUuid(row);
        const profilePath = getInvestorProfilePath(investorUuid, partnerUuid);
        return (
          <div className="leading-tight">
            <ProfileLink
              to={profilePath}
              className="whitespace-nowrap text-sm font-medium"
            >
              {row.investor_name || '—'}
            </ProfileLink>
            {row.investor_email ? (
              <div className="whitespace-nowrap text-xs text-gray-500">
                {row.investor_email}
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Product',
      key: 'product',
      width: 240,
      render: (_, row) => (
        <Space size="small" className="whitespace-nowrap">
          {row.product_logo ? (
            <Avatar src={row.product_logo} size={28} shape="square" />
          ) : null}
          <span>{row.product_name}</span>
        </Space>
      ),
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 72,
      sorter: true,
      align: 'right',
    },
    {
      title: 'Fulfilled price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Total consideration amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 220,
      align: 'right',
      sorter: true,
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Total payable',
      key: 'total_payable',
      width: 140,
      align: 'right',
      render: (_, row) => {
        const payable = formatCurrency(row.total_payable);
        const stamp = row.stamp_duty;
        const tcs = row.tcs;
        const hasBreakdown =
          (stamp != null && Number(stamp) !== 0) || (tcs != null && Number(tcs) !== 0);
        if (!hasBreakdown) return payable;
        return (
          <Tooltip
            title={
              <div className="text-xs">
                <div>Stamp: {formatCurrency(stamp)}</div>
                <div>TCS: {formatCurrency(tcs)}</div>
              </div>
            }
          >
            <span className="cursor-help border-b border-dotted border-gray-400">
              {payable}
            </span>
          </Tooltip>
        );
      },
    },
    ...(canViewAltcasePrice
      ? [
          {
            title: 'Altcase price',
            key: 'altcase_price',
            width: 200,
            render: (_, row) => (
              <AltcasePriceCell row={row} canManage={canManage} onAssigned={onAssigned} />
            ),
          },
        ]
      : []),
    {
      title: 'RM price',
      key: 'rm_price',
      width: 140,
      render: (_, row) => (
        <RmPriceCell row={row} canManage={canManage} onUpdated={onUpdated} />
      ),
    },
    {
      title: 'Partner price',
      dataIndex: 'price_partner',
      key: 'price_partner',
      width: 130,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'Partner name',
      dataIndex: 'partner_name',
      key: 'partner_name',
      width: 150,
      render: (value, row) => {
        const partnerPath = getPartnerDetailsPath(resolvePartnerUuid(row));
        return (
          <ProfileLink to={partnerPath} className="whitespace-nowrap text-sm font-medium">
            {value || '—'}
          </ProfileLink>
        );
      },
    },
    {
      title: 'RM name',
      dataIndex: 'rm_name',
      key: 'rm_name',
      width: 130,
      render: (value) => value || '—',
    },
    {
      title: 'Created by',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 120,
      render: (value) => value || '—',
    },
    {
      title: 'Payment confirmed',
      dataIndex: 'payment_confirmed_at',
      key: 'payment_confirmed_at',
      width: 160,
      render: (value) => formatDate(value),
    },
    {
      title: 'Deal Slip Signed',
      dataIndex: 'digio_signed_status',
      key: 'digio_signed_status',
      width: 100,
      render: (value) => (value ? <Tag>{value}</Tag> : '—'),
    },
    {
      title: 'UTR',
      dataIndex: 'utr_number',
      key: 'utr_number',
      width: 140,
      render: (value) => <UtrCell value={value} />,
    },
    {
      title: 'Investment ref',
      key: 'investment_reference_number',
      width: 160,
      render: (_, row) => row.investment_reference_number || '—',
    },
    ...(canViewSupplier
      ? [
          {
            title: 'Supplier',
            dataIndex: 'real_supplier_name',
            key: 'real_supplier_name',
            width: 120,
            render: (value) => value || '—',
          },
        ]
      : []),
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 220,
      sorter: true,
      render: (_, row) => (
        <div className="flex flex-col gap-0.5">
          <Tag color={resolveOrderStatusColor(row)} className="m-0 w-fit">
            {resolveOrderStatusLabel(row)}
          </Tag>
          {row.payment_pending && row.days_since_transfer != null ? (
            <span className="text-xs text-gray-500">{row.days_since_transfer}d unpaid</span>
          ) : null}
          {row.exclude_from_reports ? (
            <Tag color="orange" className="m-0 w-fit">
              Excluded from reports
            </Tag>
          ) : null}
        </div>
      ),
    },
    ...(showReportExclusionColumn
      ? [
          {
            title: 'Report exclusion',
            key: 'exclude_from_reports',
            width: 130,
            render: (_, row) =>
              row.exclude_from_reports ? (
                <Tag color="orange">Excluded</Tag>
              ) : (
                '—'
              ),
          },
        ]
      : []),
    {
      title: 'Shares transferred date',
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 190,
      render: (value) => formatDate(value),
    },
    // {
    //   title: 'Offer',
    //   key: 'offer',
    //   width: 160,
    //   render: (_, row) => formatOffer(row),
    // },
  ];
}

function AccountDetailRow({ label, value, mono = false }) {
  return (
    <div className="border-b border-gray-100 px-1 py-3 last:border-b-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p
        className={`mt-1 text-sm font-medium text-gray-900 ${mono ? 'break-all tabular-nums' : ''}`}
      >
        {value || '—'}
      </p>
    </div>
  );
}

function OrderBankDematDetails({ record }) {
  const val = (v) => (v != null && String(v).trim() !== '' ? String(v) : '—');

  return (
    <div className="space-y-4">
      <section className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50/60">
        <div className="flex items-center gap-2 border-b border-gray-200/80 bg-white px-4 py-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <BankOutlined />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-900">Bank account</p>
            <p className="text-xs text-gray-500">Linked to this order</p>
          </div>
        </div>
        <div className="px-4 py-1">
          <div className="rounded-lg border border-gray-100 bg-white px-3">
            <AccountDetailRow label="Bank" value={val(record.order_bank_name)} />
            <AccountDetailRow label="Account number" value={val(record.order_bank_account_number)} mono />
            <AccountDetailRow label="IFSC" value={val(record.order_bank_ifsc_code)} mono />
            <AccountDetailRow label="Beneficiary" value={val(record.order_bank_beneficiary_name)} />
          </div>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50/60">
        <div className="flex items-center gap-2 border-b border-gray-200/80 bg-white px-4 py-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
            <WalletOutlined />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-900">Demat account</p>
            <p className="text-xs text-gray-500">Share transfer destination</p>
          </div>
        </div>
        <div className="px-4 py-1 pb-4">
          <div className="rounded-lg border border-gray-100 bg-white px-3">
            <AccountDetailRow label="Demat number" value={val(record.order_demat_number)} mono />
            <AccountDetailRow label="Depository (DP)" value={val(record.order_demat_dp_name)} />
          </div>
        </div>
      </section>
    </div>
  );
}

const ANT_TO_API_SORT_FIELD = {
  created_at: 'created_at',
  order_number: 'order_number',
  quantity: 'quantity',
  total_amount: 'total_amount',
  status: 'status',
};

// ─── Excel Export: column definitions ────────────────────────────────────────
// type: 'text' (default) | 'number' | 'date'
// width: ignored at runtime (auto-calculated), kept for documentation only
export const ORDER_EXPORT_COLUMNS = [
  { header: 'Created Date',            key: 'created_at',                  type: 'date'   },
  { header: 'Folio',                   key: 'folio'                                       },
  { header: 'Order #',                 key: 'order_number'                                },
  { header: 'Deal request Id',         key: 'order_req_id'                                },
  { header: 'Investor Name',           key: 'investor_name'                               },
  { header: 'Investor Email',          key: 'investor_email'                              },
  { header: 'Investor mobile number',  key: 'investor_mobile'                             },
  { header: 'Investor PAN',            key: 'investor_pan'                                },
  { header: 'Demat Account No.',       key: 'demat_number'                                },
  { header: 'Product ISIN',            key: 'isin'                                        },
  { header: 'Product',                 key: 'product_name'                                },
  { header: 'Quantity',                key: 'quantity',                    type: 'number' },
  { header: 'Fulfilled Price (₹)',     key: 'unit_price',                  type: 'number' },
  { header: 'Total Consideration (₹)', key: 'total_amount',                type: 'number' },
  { header: 'Stamp Duty (₹)',          key: 'stamp_duty',                  type: 'number' },
  { header: 'TCS (₹)',                 key: 'tcs',                         type: 'number' },
  { header: 'Total Payable (₹)',       key: 'total_payable',               type: 'number' },
  { header: 'UTR',                     key: 'utr_number'                                  },
  { header: 'Bank name',               key: 'bank_name'                                   },
  { header: 'Bank IFSC',               key: 'bank_isin'                                   },
  { header: 'Bank Account No.',        key: 'bank_account_number'                         },
  { header: 'Bank Beneficiary',        key: 'bank_beneficiary_name'                       },
  { header: 'Depository (DP)',         key: 'demat_dp_name'                               },
  { header: 'Partner Price (₹)',       key: 'price_partner',               type: 'number' },
  { header: 'Partner Name',            key: 'partner_name'                                },
  { header: 'RM Name',                 key: 'rm_name'                                     },
  { header: 'Altcase Sell Price (₹)',  key: 'altcase_price',               type: 'number' },
  { header: 'RM Sell Price (₹)',       key: 'rm_price',                    type: 'number' },
  { header: 'Investment Ref',          key: 'investment_reference_number'                 },
  { header: 'Supplier',                key: 'real_supplier_name'                          },
  { header: 'Created By',              key: 'created_by_name'                             },
  { header: 'Status',                  key: 'status'                                      },
  { header: 'Payment Confirmed Date',  key: 'payment_confirmed_at',        type: 'date'   },
  { header: 'Shares Transferred Date', key: 'completed_at',                type: 'date'   },
  { header: 'Deal Slip Signed',        key: 'digio_signed_status'                         },
];

const SALES_HIDDEN_ORDER_EXPORT_KEYS = new Set([
  'altcase_price',
  'rm_price',
  'created_by_name',
  'real_supplier_name',
]);

export const getOrderExportColumns = (entityType) => {
  if (entityType === 'SALES') {
    return ORDER_EXPORT_COLUMNS.filter((column) => !SALES_HIDDEN_ORDER_EXPORT_KEYS.has(column.key));
  }
  return ORDER_EXPORT_COLUMNS;
};

// ─── Excel Export: row transformer ───────────────────────────────────────────
export const mapOrderExportRow = (row) => ({
  created_at:                  formatDate(row.created_at),
  folio:                       row.folio_number ?? (row.folio_id ? String(row.folio_id) : ''),
  order_number:                row.order_number ?? '',
  order_req_id:                row.order_req_id ?? '',
  investor_name:               row.investor_name ?? '',
  investor_email:              row.investor_email ?? '',
  investor_mobile:             row.investor_mobile ?? row.investor_phone ?? '',
  investor_pan:                row.investor_pan ?? '',
  demat_number:                row.order_demat_number ?? '',
  isin:                        row.isin ?? '',
  product_name:                row.product_name ?? '',
  quantity:                    row.quantity != null ? Number(row.quantity) : '',
  unit_price:                  row.unit_price != null ? Number(row.unit_price) : '',
  total_amount:                row.total_amount != null ? Number(row.total_amount) : '',
  stamp_duty:                  row.stamp_duty != null ? Number(row.stamp_duty) : '',
  tcs:                         row.tcs != null ? Number(row.tcs) : '',
  total_payable:               row.total_payable != null ? Number(row.total_payable) : '',
  utr_number:                  row.utr_number ?? '',
  bank_name:                   row.order_bank_name ?? '',
  bank_isin:                   row.order_bank_ifsc_code ?? '',
  bank_account_number:         row.order_bank_account_number ?? '',
  bank_beneficiary_name:       row.order_bank_beneficiary_name ?? '',
  demat_dp_name:               row.order_demat_dp_name ?? '',
  price_partner:               row.price_partner != null ? Number(row.price_partner) : '',
  partner_name:                row.partner_name ?? '',
  rm_name:                     row.rm_name ?? '',
  altcase_price:               row.altcase_price != null ? Number(row.altcase_price) : '',
  rm_price:                    row.rm_price != null ? Number(row.rm_price) : '',
  investment_reference_number: row.investment_reference_number ?? '',
  real_supplier_name:          row.real_supplier_name ?? '',
  created_by_name:             row.created_by_name ?? '',
  status:                      resolveOrderStatusLabel(row),
  payment_confirmed_at:        formatDate(row.payment_confirmed_at),
  completed_at:                formatDate(row.completed_at),
  digio_signed_status:         row.digio_signed_status ?? '',
});

const DEFAULT_SORT = { field: 'created_at', order: 'desc' };

const getDefaultCreatedRange = () => {
  const today = dayjs();
  const fyStartYear = today.month() >= 3 ? today.year() : today.year() - 1;
  return [today.year(fyStartYear).month(3).date(1).startOf('day'), today];
};

const isDefaultCreatedRange = (range) => {
  if (!range?.[0] || !range?.[1]) return range == null;
  const [defaultStart, defaultEnd] = getDefaultCreatedRange();
  return range[0].isSame(defaultStart, 'day') && range[1].isSame(defaultEnd, 'day');
};

const parseCommaParam = (value) => {
  if (!value) return [];
  return String(value)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
};

const joinCommaParam = (values) => {
  if (!Array.isArray(values) || values.length === 0) return null;
  return values.join(',');
};

const formatMultiFilterLabels = (values, options, fallback = (value) => value) =>
  values
    .map((value) => options.find((option) => String(option.value) === String(value))?.label || fallback(value))
    .join(', ');

function formatInr(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function formatQty(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-IN');
}

function PaymentReceivableSummaryModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [dryRunData, setDryRunData] = useState(null);
  const [configError, setConfigError] = useState('');

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const loadDryRun = async () => {
      setLoading(true);
      setDryRunData(null);
      setConfigError('');
      try {
        const res = await orderService.sendPaymentReceivableSummary({ dryRun: true });
        if (cancelled) return;
        if (res?.success === false) {
          setConfigError(res?.message || 'Failed to load payment receivable summary');
          return;
        }
        setDryRunData(res?.data ?? null);
      } catch (err) {
        if (!cancelled) {
          setConfigError(
            err?.response?.data?.message || err?.message || 'Failed to load payment receivable summary',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDryRun();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const summary = dryRunData?.summary;
  const rows = summary?.rows ?? [];
  const targetGroups = dryRunData?.target_groups ?? [];
  const hasRows = rows.length > 0;

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await orderService.sendPaymentReceivableSummary({ dryRun: false });
      if (res?.success === false) {
        message.error(res?.message || 'Failed to send');
        return;
      }
      const data = res?.data ?? {};
      if (data.skipped) {
        message.warning('No payment receivable orders to send');
        onClose();
        return;
      }
      const sent = Number(data.sent) || 0;
      const failed = Number(data.failed) || 0;
      if (failed > 0) {
        message.error(`Sent to ${sent} group(s), ${failed} failed`);
      } else {
        message.success(`Payment receivable sent to ${sent} WhatsApp group(s)`);
      }
      onClose();
    } catch (err) {
      message.error(
        err?.response?.data?.message || err?.message || 'Failed to send',
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title="Payment Receivable"
      open={open}
      onCancel={onClose}
      onOk={handleSend}
      okText="Send to WhatsApp"
      okButtonProps={{
        disabled: loading || !!configError || !hasRows || targetGroups.length === 0,
        loading: sending,
      }}
      cancelButtonProps={{ disabled: sending }}
      width={880}
      destroyOnClose
    >
      {loading && <p className="text-sm text-gray-500">Loading payment receivable...</p>}

      {configError && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <p>{configError}</p>
          <Link to="/pricing/whatsapp-groups" className="text-green-700 font-medium underline mt-2 inline-block">
            Configure WhatsApp Groups
          </Link>
        </div>
      )}

      {summary && !configError && !hasRows && (
        <p className="text-sm text-gray-600">
          No orders with shares transferred and payment still pending.
        </p>
      )}

      {summary && !configError && hasRows && (
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-[10px] uppercase text-gray-400 font-semibold">Orders</div>
              <div className="text-lg font-bold tabular-nums">{summary.totals?.orders ?? rows.length}</div>
            </div>
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-[10px] uppercase text-gray-400 font-semibold">Receivable</div>
              <div className="text-lg font-bold tabular-nums">{formatInr(summary.totals?.total_receivable)}</div>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="text-[10px] uppercase text-amber-700 font-semibold">Overdue (&gt;7d)</div>
              <div className="text-lg font-bold tabular-nums text-amber-800">{summary.totals?.overdue_orders ?? 0}</div>
            </div>
          </div>

          <div className="overflow-x-auto max-h-72 overflow-y-auto border border-gray-100 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-gray-500">Investor</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-500">Partner</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-500">RM</th>
                  <th className="text-left px-3 py-2 font-semibold text-gray-500">Product</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-500">Qty</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-500">Receivable</th>
                  <th className="text-right px-3 py-2 font-semibold text-gray-500">Days unpaid</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.order_uuid || row.order_number} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-medium text-gray-800">{row.investor_name}</td>
                    <td className="px-3 py-2 text-gray-700">{row.partner_name}</td>
                    <td className="px-3 py-2 text-gray-700">{row.rm_name}</td>
                    <td className="px-3 py-2 text-gray-700">{row.product_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatQty(row.quantity)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{formatInr(row.receivable)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${Number(row.days_unpaid) >= 7 ? 'text-amber-700' : 'text-gray-800'}`}>
                      {row.days_unpaid}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-gray-600">
            Will send to <strong>{targetGroups.length}</strong> WhatsApp group
            {targetGroups.length === 1 ? '' : 's'}
            {' '}
            (inventory position groups):
            {' '}
            {targetGroups.map((g) => g.group_name).join(', ') || '—'}
          </p>
        </div>
      )}
    </Modal>
  );
}

export default function OrderManagementPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isSuperAdmin, user } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(PERMISSIONS.ORDERS_MANAGE);
  const canViewAltcasePrice =
    isSuperAdmin || hasPermission(PERMISSIONS.VIEW_ORDERS_ALTCASE_PRICE);
  const canViewSupplier =
    isSuperAdmin || hasPermission(PERMISSIONS.VIEW_ORDER_SUPPLIER);
  const canEditOrderQuantityPermission =
    isSuperAdmin ||
    hasPermission(PERMISSIONS.ORDERS_QUANTITY_EDIT) ||
    hasPermission(PERMISSIONS.ORDERS_MANAGE);
  const canEditOrderPartnerPricePermission =
    isSuperAdmin ||
    hasPermission(PERMISSIONS.ORDERS_PARTNER_PRICE_EDIT) ||
    hasPermission(PERMISSIONS.ORDERS_CHANGE_PARTNER_PRICE) ||
    hasPermission(PERMISSIONS.ORDERS_MANAGE);
  const canManageReportExclusion =
    isSuperAdmin || hasPermission(PERMISSIONS.ORDERS_REPORT_EXCLUSION_MANAGE);
  const orderExportColumns = useMemo(
    () => getOrderExportColumns(user?.entity_type),
    [user?.entity_type]
  );

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // ── Filter / sort / pagination — seeded from URL on first render ──────────
  const [page, setPage] = useState(() => {
    const v = Number(searchParams.get('page'));
    return v > 0 ? v : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const v = Number(searchParams.get('limit'));
    return v > 0 ? v : 10;
  });
  const [search, setSearch] = useState(() => searchParams.get('q') || '');
  const [statusFilter, setStatusFilter] = useState(() => parseCommaParam(searchParams.get('status')));
  const [paymentPendingFilter, setPaymentPendingFilter] = useState(
    () => searchParams.get('payment_pending') === 'true'
  );
  const [rmFilter, setRmFilter] = useState(() => parseCommaParam(searchParams.get('rm')));
  const [partnerFilter, setPartnerFilter] = useState(() => searchParams.get('partner') || null);
  const [productFilter, setProductFilter] = useState(() => parseCommaParam(searchParams.get('product')));
  const [selectedPartnerRecord, setSelectedPartnerRecord] = useState(null);
  const [selectedProductRecords, setSelectedProductRecords] = useState([]);
  const initialPartnerFilterRef = useRef(searchParams.get('partner')?.trim() || '');
  const initialProductFiltersRef = useRef(parseCommaParam(searchParams.get('product')));
  const partnerUrlPrefillDoneRef = useRef(false);
  const productUrlPrefillDoneRef = useRef(false);
  const [rmOptions, setRmOptions] = useState([]);
  const [loadingRms, setLoadingRms] = useState(false);
  const [priceMin, setPriceMin] = useState(() => {
    const v = searchParams.get('price_min');
    return v !== null && v !== '' ? Number(v) : null;
  });
  const [priceMax, setPriceMax] = useState(() => {
    const v = searchParams.get('price_max');
    return v !== null && v !== '' ? Number(v) : null;
  });
  const [createdRange, setCreatedRange] = useState(() => {
    const f = searchParams.get('date_from');
    const t = searchParams.get('date_to');
    return f && t ? [dayjs(f), dayjs(t)] : getDefaultCreatedRange();
  });
  const [transferRange, setTransferRange] = useState(() => {
    const f = searchParams.get('tf_from');
    const t = searchParams.get('tf_to');
    return f && t ? [dayjs(f), dayjs(t)] : null;
  });
  const [sort, setSort] = useState(() => ({
    field: searchParams.get('sort_by') || DEFAULT_SORT.field,
    order: searchParams.get('sort_order') || DEFAULT_SORT.order,
  }));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerOrder, setDrawerOrder] = useState(null);
  const [drawerOrderId, setDrawerOrderId] = useState(null);

  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [utrOpen, setUtrOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [excludeReportsOpen, setExcludeReportsOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editPricingLoading, setEditPricingLoading] = useState(false);
  const [actionTarget, setActionTarget] = useState(null);
  const [confirmForm] = Form.useForm();
  const [utrForm] = Form.useForm();
  const [cancelForm] = Form.useForm();
  const [excludeReportsForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const editPricingAbortRef = useRef(null);
  const editPricingDebounceRef = useRef(null);
  const editPricingRequestIdRef = useRef(0);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm] = Form.useForm();
  const [reconcileDrawerOpen, setReconcileDrawerOpen] = useState(false);
  const [accountsModalOpen, setAccountsModalOpen] = useState(false);
  const [accountsModalRow, setAccountsModalRow] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [paymentReceivableOpen, setPaymentReceivableOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const fetchPartnersPage = useCallback(async ({ page, limit, search: partnerSearch, signal }) => {
    const res = await ifaService.getAdminIfas({
      page,
      limit,
      search: partnerSearch,
      status: 'active',
      signal,
    });
    return {
      items: Array.isArray(res?.users) ? res.users : [],
      pagination: res?.pagination,
    };
  }, []);

  const fetchProductsPage = useCallback(async ({ page, limit, search: productSearch, signal }) => {
    const response = await productService.getProducts({
      type: 'PRE_IPO',
      page,
      limit,
      search: productSearch,
      sort_by: 'preference_index',
      sort_order: 'asc',
      signal,
    });
    const data = response?.data ?? response;
    const list = data?.products ?? data?.data ?? data?.items ?? [];
    return {
      items: Array.isArray(list) ? list : [],
      pagination: data?.pagination,
    };
  }, []);

  const {
    items: partners,
    loading: loadingPartners,
    handleSearch: handlePartnerSearch,
    handlePopupScroll: handlePartnerPopupScroll,
    handleOpenChange: handlePartnerOpenChange,
    dropdownRender: partnerDropdownRender,
    searchValue: partnerSearchValue,
    clearSearch: clearPartnerSearch,
  } = useRemoteSelectPage({
    fetchPage: fetchPartnersPage,
    getKey: getPartnerUuid,
    pageSize: SELECT_PAGE_SIZE,
    selectedValue: partnerFilter,
    prefetchOnMount: Boolean(initialPartnerFilterRef.current),
    prefetchSearch: initialPartnerFilterRef.current,
    onError: (e) => message.error(e?.response?.data?.message || e?.message || 'Failed to load partners'),
  });

  const {
    items: products,
    loading: loadingProducts,
    handleSearch: handleProductSearch,
    handlePopupScroll: handleProductPopupScroll,
    handleOpenChange: handleProductOpenChange,
    dropdownRender: productDropdownRender,
    searchValue: productSearchValue,
    clearSearch: clearProductSearch,
  } = useRemoteSelectPage({
    fetchPage: fetchProductsPage,
    getKey: getProductId,
    pageSize: SELECT_PAGE_SIZE,
    selectedValue: productFilter[0] ?? null,
    prefetchOnMount: Boolean(initialProductFiltersRef.current.length),
    prefetchSearch: initialProductFiltersRef.current[0] || '',
    onError: (e) => message.error(e?.response?.data?.message || e?.message || 'Failed to load products'),
  });

  const partnerOptions = useMemo(() => {
    const base = partners
      .map((partner) => {
        const value = getPartnerUuid(partner);
        if (!value) return null;
        return {
          value: String(value),
          label: partner.name || partner.email || String(value),
          partner,
        };
      })
      .filter(Boolean);

    if (
      partnerFilter &&
      selectedPartnerRecord &&
      String(getPartnerUuid(selectedPartnerRecord)) === String(partnerFilter) &&
      !base.some((option) => String(option.value) === String(partnerFilter))
    ) {
      return [
        {
          value: String(partnerFilter),
          label: selectedPartnerRecord.name || selectedPartnerRecord.email || String(partnerFilter),
          partner: selectedPartnerRecord,
        },
        ...base,
      ];
    }
    return base;
  }, [partners, partnerFilter, selectedPartnerRecord]);

  const productOptions = useMemo(() => {
    const base = products
      .map((product) => {
        const value = getProductId(product);
        if (!value) return null;
        return {
          value: String(value),
          label: product.name || product.short_name || String(value),
          product,
        };
      })
      .filter(Boolean);

    const extras = productFilter
      .filter((id) => !base.some((option) => String(option.value) === String(id)))
      .map((id) => {
        const record = selectedProductRecords.find(
          (product) => String(getProductId(product)) === String(id)
        );
        return {
          value: String(id),
          label: record?.name || record?.short_name || String(id),
          product: record,
        };
      });

    return extras.length ? [...extras, ...base] : base;
  }, [products, productFilter, selectedProductRecords]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        search: search || undefined,
        status: joinCommaParam(statusFilter) || undefined,
        payment_pending: paymentPendingFilter ? true : undefined,
        rm_uuid: joinCommaParam(rmFilter) || undefined,
        partner_uuid: partnerFilter || undefined,
        product_id: joinCommaParam(productFilter) || undefined,
        altcase_price_min: priceMin ?? undefined,
        altcase_price_max: priceMax ?? undefined,
        date_from: createdRange?.[0]?.startOf('day').toISOString(),
        date_to: createdRange?.[1]?.endOf('day').toISOString(),
        transfer_date_from: transferRange?.[0]?.startOf('day').toISOString(),
        transfer_date_to: transferRange?.[1]?.endOf('day').toISOString(),
        sort_by: sort.field,
        sort_order: sort.order,
      };
      const result = await orderService.adminListOrders(params);
      setOrders(result?.orders ?? []);
      setTotal(result?.pagination?.total ?? 0);
    } catch (e) {
      message.error(
        e?.response?.data?.message || e?.message || 'Failed to load orders'
      );
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    search,
    statusFilter,
    paymentPendingFilter,
    rmFilter,
    partnerFilter,
    productFilter,
    priceMin,
    priceMax,
    createdRange,
    transferRange,
    sort,
  ]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Stable reference that always calls the latest loadOrders without being
  // listed as a dep of useMemo(columns). This prevents columns from being
  // recreated every time a filter changes, which was causing Ant Design to
  // accumulate `ant-table-column-has-sorters` on every re-render.
  const loadOrdersRef = React.useRef(loadOrders);
  useEffect(() => { loadOrdersRef.current = loadOrders; }, [loadOrders]);
  const stableLoadOrders = useCallback(() => loadOrdersRef.current(), []);

  // ── Sync all filter / sort / pagination state → URL (replace, no history spam) ──
  useEffect(() => {
    const p = {};
    if (search)              p.q          = search;
    const statusParam = joinCommaParam(statusFilter);
    if (statusParam)           p.status     = statusParam;
    if (paymentPendingFilter)  p.payment_pending = 'true';
    const rmParam = joinCommaParam(rmFilter);
    if (rmParam)               p.rm         = rmParam;
    if (partnerFilter)         p.partner    = partnerFilter;
    const productParam = joinCommaParam(productFilter);
    if (productParam)          p.product    = productParam;
    if (priceMin != null)    p.price_min  = priceMin;
    if (priceMax != null)    p.price_max  = priceMax;
    if (createdRange?.[0])   p.date_from  = createdRange[0].format('YYYY-MM-DD');
    if (createdRange?.[1])   p.date_to    = createdRange[1].format('YYYY-MM-DD');
    if (transferRange?.[0])  p.tf_from    = transferRange[0].format('YYYY-MM-DD');
    if (transferRange?.[1])  p.tf_to      = transferRange[1].format('YYYY-MM-DD');
    if (page > 1)            p.page       = page;
    if (pageSize !== 10)     p.limit      = pageSize;
    if (sort.field !== DEFAULT_SORT.field)   p.sort_by    = sort.field;
    if (sort.order !== DEFAULT_SORT.order)   p.sort_order = sort.order;
    setSearchParams(p, { replace: true });
  }, [search, statusFilter, paymentPendingFilter, rmFilter, partnerFilter, productFilter, priceMin, priceMax, createdRange, transferRange, page, pageSize, sort, setSearchParams]);

  useEffect(() => {
    if (!partnerFilter || !partners.length) return;
    const match = partners.find((partner) => String(getPartnerUuid(partner)) === String(partnerFilter));
    if (match) setSelectedPartnerRecord(match);
  }, [partnerFilter, partners]);

  useEffect(() => {
    if (!productFilter.length || !products.length) return;
    const matches = products.filter((product) =>
      productFilter.includes(String(getProductId(product)))
    );
    if (!matches.length) return;
    setSelectedProductRecords((prev) => {
      let changed = false;
      const next = [...prev];
      matches.forEach((match) => {
        const id = String(getProductId(match));
        if (!next.some((product) => String(getProductId(product)) === id)) {
          next.push(match);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [productFilter, products]);

  useEffect(() => {
    if (!initialPartnerFilterRef.current || partnerUrlPrefillDoneRef.current || !partners.length) return;
    const match = partners.find(
      (partner) => String(getPartnerUuid(partner)) === String(initialPartnerFilterRef.current)
    );
    if (!match) return;
    partnerUrlPrefillDoneRef.current = true;
    setSelectedPartnerRecord(match);
    clearPartnerSearch();
  }, [partners, clearPartnerSearch]);

  useEffect(() => {
    if (!initialProductFiltersRef.current.length || productUrlPrefillDoneRef.current || !products.length) return;
    const matches = products.filter((product) =>
      initialProductFiltersRef.current.includes(String(getProductId(product)))
    );
    if (!matches.length) return;
    productUrlPrefillDoneRef.current = true;
    setSelectedProductRecords(matches);
    clearProductSearch();
  }, [products, clearProductSearch]);

  useEffect(() => {
    let cancelled = false;
    setLoadingRms(true);
    orderService
      .listOrderRms()
      .then((rms) => {
        if (cancelled) return;
        setRmOptions(
          rms
            .map((rm) => {
              const value = rm?.id ?? rm?.uuid;
              if (!value) return null;
              return { value: String(value), label: formatRmLabel(rm?.name) };
            })
            .filter(Boolean)
        );
      })
      .catch((e) => {
        if (cancelled) return;
        message.error(e?.response?.data?.message || e?.message || 'Failed to load RMs');
        setRmOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingRms(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openDetailDrawer = useCallback(async (row) => {
    const id = row.id || row.uuid;
    if (!id) return;
    setDrawerOpen(true);
    setDrawerOrderId(id);
    setDrawerLoading(true);
    setDrawerOrder(null);
    try {
      const res = await orderService.adminGetOrderDetail(id);
      const order = res?.order ?? res;
      setDrawerOrder(order);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to load order');
      setDrawerOpen(false);
      setDrawerOrderId(null);
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  const refreshDrawerIfOpen = useCallback(
    async (oid) => {
      if (!drawerOpen || !oid || oid !== drawerOrderId) return;
      setDrawerLoading(true);
      try {
        const res = await orderService.adminGetOrderDetail(oid);
        setDrawerOrder(res?.order ?? res);
      } catch {
        /* keep stale detail */
      } finally {
        setDrawerLoading(false);
      }
    },
    [drawerOpen, drawerOrderId]
  );

  const orderIdOf = orderRowId;

  const runTransferShares = async () => {
    if (!actionTarget) return;
    const oid = orderIdOf(actionTarget);
    const orderUuid = actionTarget.uuid || actionTarget.id;
    try {
      const values = await transferForm.validateFields();
      setActionSubmitting(true);
      await orderService.transferShares(orderUuid, {
        transfer_date: values.transfer_date.startOf('day').toISOString(),
        remarks: values.remarks?.trim() || undefined,
      });
      message.success('Shares marked as transferred');
      setTransferOpen(false);
      transferForm.resetFields();
      setActionTarget(null);
      await loadOrders();
      await refreshDrawerIfOpen(oid);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(
        e?.response?.data?.message || e?.message || 'Transfer failed'
      );
    } finally {
      setActionSubmitting(false);
    }
  };

  const runConfirmPayment = async () => {
    if (!actionTarget) return;
    const oid = orderIdOf(actionTarget);
    try {
      const values = await confirmForm.validateFields();
      setActionSubmitting(true);
      await orderService.confirmPayment(oid, {
        utr_number: values.utr_number?.trim() || undefined,
      });
      message.success('Payment confirmed');
      setConfirmOpen(false);
      confirmForm.resetFields();
      setActionTarget(null);
      await loadOrders();
      await refreshDrawerIfOpen(oid);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Confirm failed');
    } finally {
      setActionSubmitting(false);
    }
  };

  const runUpdateUtr = async () => {
    if (!actionTarget) return;
    const oid = orderIdOf(actionTarget);
    try {
      const values = await utrForm.validateFields();
      setActionSubmitting(true);
      await orderService.updateUtr(oid, values.utr_number.trim());
      message.success('UTR updated');
      setUtrOpen(false);
      utrForm.resetFields();
      setActionTarget(null);
      await loadOrders();
      await refreshDrawerIfOpen(oid);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Update failed');
    } finally {
      setActionSubmitting(false);
    }
  };

  const runCancel = async () => {
    if (!actionTarget) return;
    const oid = orderIdOf(actionTarget);
    try {
      const values = await cancelForm.validateFields();
      setActionSubmitting(true);
      await orderService.cancelOrder(oid, values.remarks);
      message.success('Order cancelled');
      setCancelOpen(false);
      cancelForm.resetFields();
      setActionTarget(null);
      await loadOrders();
      await refreshDrawerIfOpen(oid);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Cancel failed');
    } finally {
      setActionSubmitting(false);
    }
  };

  const runExcludeFromReports = async () => {
    if (!actionTarget) return;
    const oid = orderIdOf(actionTarget);
    try {
      const values = await excludeReportsForm.validateFields();
      setActionSubmitting(true);
      await orderService.updateReportExclusion(oid, {
        exclude: true,
        reason: values.reason,
      });
      message.success('Order excluded from reports and dashboard.');
      setExcludeReportsOpen(false);
      excludeReportsForm.resetFields();
      setActionTarget(null);
      await loadOrders();
      await refreshDrawerIfOpen(oid);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(
        e?.response?.data?.message || e?.message || 'Could not exclude order from reports.'
      );
    } finally {
      setActionSubmitting(false);
    }
  };

  const confirmRestoreReportExclusion = useCallback(
    (row) => {
      Modal.confirm({
        title: 'Restore order to reports?',
        content:
          'Revenue from this order will count again on the dashboard and leadership report exports. ' +
          'Team-scoped RMs will see this order in their list again.',
        okText: 'Restore to reports',
        onOk: async () => {
          const oid = orderIdOf(row);
          setActionSubmitting(true);
          try {
            await orderService.updateReportExclusion(oid, { exclude: false });
            message.success('Order restored to reports and dashboard.');
            await loadOrders();
            await refreshDrawerIfOpen(oid);
          } catch (e) {
            message.error(
              e?.response?.data?.message || e?.message || 'Could not restore order to reports.'
            );
            throw e;
          } finally {
            setActionSubmitting(false);
          }
        },
      });
    },
    [loadOrders, refreshDrawerIfOpen]
  );

  const runEditOrder = async () => {
    if (!actionTarget) return;
    const oid = orderIdOf(actionTarget);
    const status = actionTarget.status;
    const quantityEditable =
      canEditOrderQuantityPermission && canEditOrderQuantity(status);
    const partnerPriceEditable =
      canEditOrderPartnerPricePermission && canEditOrderPartnerPrice(status);
    try {
      const fieldsToValidate = [];
      if (quantityEditable) fieldsToValidate.push('quantity');
      if (partnerPriceEditable) fieldsToValidate.push('partner_price');
      const values = await editForm.validateFields(fieldsToValidate);
      const payload = {};
      if (quantityEditable) {
        payload.quantity = Number(values.quantity);
      }
      if (partnerPriceEditable) {
        payload.partner_price = Number(values.partner_price);
      }
      if (!Object.keys(payload).length) return;
      setActionSubmitting(true);
      await orderService.updateOrderFields(oid, payload);
      message.success('Order updated');
      setEditOpen(false);
      editForm.resetFields();
      setActionTarget(null);
      await loadOrders();
      await refreshDrawerIfOpen(oid);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || e?.message || 'Update failed');
    } finally {
      setActionSubmitting(false);
    }
  };

  const runDuplicateOfferExpiredOrder = async () => {
    if (!actionTarget) return;
    const oid = orderIdOf(actionTarget);
    try {
      setActionSubmitting(true);
      const data = await orderService.duplicateOfferExpiredOrder(oid);
      message.success('Order duplicated successfully');
      setDuplicateOpen(false);
      setActionTarget(null);
      await loadOrders();
      if (data?.order?.id) {
        openDetailDrawer({ id: data.order.id });
      }
    } catch (e) {
      message.error(
        e?.response?.data?.message || e?.message || 'Duplicate failed'
      );
    } finally {
      setActionSubmitting(false);
    }
  };

  const runResolveStockPending = useCallback((row) => {
    const oid = orderIdOf(row);
    const shortfall = row.stock_pending_shortfall;
    Modal.confirm({
      title: 'Resolve stock',
      content:
        shortfall != null && Number(shortfall) > 0
          ? `Hold the remaining ${shortfall} units from fake inventory and mark this order as Payment Received?`
          : 'Complete the fake inventory hold and mark this order as Payment Received?',
      okText: 'Resolve',
      onOk: async () => {
        try {
          await orderService.resolveStockPending(oid);
          message.success('Stock resolved — order is ready for share transfer');
          await loadOrders();
          await refreshDrawerIfOpen(oid);
        } catch (e) {
          const opsAction = e?.response?.data?.error?.ops_action;
          message.error(
            opsAction || e?.response?.data?.message || e?.message || 'Resolve failed'
          );
          throw e;
        }
      },
    });
  }, [loadOrders, refreshDrawerIfOpen]);

  const openConfirm = useCallback((row) => {
    setActionTarget(row);
    confirmForm.setFieldsValue({ utr_number: row.utr_number || '' });
    setConfirmOpen(true);
  }, [confirmForm]);

  const openUtr = useCallback((row) => {
    setActionTarget(row);
    utrForm.setFieldsValue({ utr_number: row.utr_number || '' });
    setUtrOpen(true);
  }, [utrForm]);

  const openCancel = useCallback((row) => {
    setActionTarget(row);
    cancelForm.resetFields();
    setCancelOpen(true);
  }, [cancelForm]);

  const openExcludeFromReports = useCallback((row) => {
    setActionTarget(row);
    excludeReportsForm.resetFields();
    setExcludeReportsOpen(true);
  }, [excludeReportsForm]);

  const openDuplicate = useCallback((row) => {
    setActionTarget(row);
    setDuplicateOpen(true);
  }, []);

  const openEdit = useCallback((row) => {
    setActionTarget(row);
    const partnerPrice = row.price_partner ?? row.partner_price;
    editForm.setFieldsValue({
      quantity: row.quantity != null ? Number(row.quantity) : undefined,
      partner_price:
        partnerPrice != null && Number.isFinite(Number(partnerPrice))
          ? Number(partnerPrice)
          : undefined,
    });
    setEditOpen(true);
  }, [editForm]);

  useEffect(() => {
    if (editOpen) return undefined;
    clearTimeout(editPricingDebounceRef.current);
    if (editPricingAbortRef.current) {
      editPricingAbortRef.current.abort();
      editPricingAbortRef.current = null;
    }
    setEditPricingLoading(false);
    return undefined;
  }, [editOpen]);

  const handleEditQuantityChange = useCallback(
    (value) => {
      editForm.setFieldValue('quantity', value);

      if (!actionTarget) return;

      const productId = resolveOrderProductId(actionTarget);
      const partnerId = resolvePartnerUuid(actionTarget);
      const userId = resolveInvestorUuid(actionTarget);
      if (!productId || !partnerId || !userId) return;

      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed <= 0 || !Number.isInteger(parsed)) return;

      const canPrefillPartnerPrice =
        canEditOrderPartnerPricePermission &&
        canEditOrderPartnerPrice(actionTarget.status);

      clearTimeout(editPricingDebounceRef.current);
      editPricingDebounceRef.current = setTimeout(async () => {
        if (editPricingAbortRef.current) {
          editPricingAbortRef.current.abort();
        }
        editPricingAbortRef.current = new AbortController();
        const requestId = ++editPricingRequestIdRef.current;

        if (canPrefillPartnerPrice) {
          setEditPricingLoading(true);
        }

        try {
          const data = await resolvePricing({
            productId,
            partnerId,
            userId,
            quantity: parsed,
            signal: editPricingAbortRef.current.signal,
          });
          if (requestId !== editPricingRequestIdRef.current) return;

          const slabPrice = data?.applied_pricing_slab?.pricing_per_unit;
          if (
            canPrefillPartnerPrice &&
            slabPrice != null &&
            Number.isFinite(Number(slabPrice))
          ) {
            editForm.setFieldValue('partner_price', Number(slabPrice));
          }
        } catch (e) {
          if (e?.name === 'CanceledError' || e?.name === 'AbortError') return;
        } finally {
          if (requestId === editPricingRequestIdRef.current && canPrefillPartnerPrice) {
            setEditPricingLoading(false);
          }
        }
      }, EDIT_PRICING_DEBOUNCE_MS);
    },
    [actionTarget, editForm, canEditOrderPartnerPricePermission]
  );

  const openTransfer = useCallback((row) => {
    setActionTarget(row);
    transferForm.setFieldsValue({ transfer_date: dayjs(), remarks: '' });
    setTransferOpen(true);
  }, [transferForm]);

  const handleSearch = (value) => {
    setSearch((value || '').trim());
    setPage(1);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(Array.isArray(value) && value.length ? value : []);
    setPage(1);
  };

  const handlePaymentPendingChange = (checked) => {
    setPaymentPendingFilter(Boolean(checked));
    setPage(1);
  };

  const handleRmChange = (value) => {
    setRmFilter(Array.isArray(value) && value.length ? value.map(String) : []);
    setPage(1);
  };

  const handlePartnerChange = (value) => {
    setPartnerFilter(value || null);
    setSelectedPartnerRecord(
      value
        ? partners.find((partner) => String(getPartnerUuid(partner)) === String(value)) ?? null
        : null
    );
    setPage(1);
  };

  const handleProductChange = (values) => {
    const next = Array.isArray(values) ? values.map(String) : [];
    setProductFilter(next);
    setSelectedProductRecords(
      next
        .map((id) => products.find((product) => String(getProductId(product)) === String(id)))
        .filter(Boolean)
    );
    setPage(1);
  };

  const handlePriceMinChange = (value) => {
    const next = value ?? null;
    setPriceMin(next);
    if (next != null && priceMax != null && priceMax < next) {
      setPriceMax(next);
    }
    setPage(1);
  };

  const handlePriceMaxChange = (value) => {
    const next = value ?? null;
    if (next != null && priceMin != null && next < priceMin) {
      message.warning('Maximum price cannot be less than minimum price');
      setPriceMax(priceMin);
    } else {
      setPriceMax(next);
    }
    setPage(1);
  };

  const handleCreatedRangeChange = (range) => {
    setCreatedRange(range || null);
    setPage(1);
  };

  const handleTransferRangeChange = (range) => {
    setTransferRange(range || null);
    setPage(1);
  };

  const handleReset = () => {
    setSearch('');
    setStatusFilter([]);
    setPaymentPendingFilter(false);
    setRmFilter([]);
    setPartnerFilter(null);
    setProductFilter([]);
    setSelectedPartnerRecord(null);
    setSelectedProductRecords([]);
    setPriceMin(null);
    setPriceMax(null);
    setCreatedRange(getDefaultCreatedRange());
    setTransferRange(null);
    setSort(DEFAULT_SORT);
    setPage(1);
    setPageSize(10);
  };

  const hasActiveFilters = Boolean(
    search ||
      statusFilter.length ||
      paymentPendingFilter ||
      rmFilter.length ||
      partnerFilter ||
      productFilter.length ||
      priceMin != null ||
      priceMax != null ||
      (createdRange && !isDefaultCreatedRange(createdRange)) ||
      transferRange
  );

  // Count only the drawer-managed filters so the badge reflects hidden state.
  const filterDrawerCount = [
    statusFilter.length > 0,
    paymentPendingFilter,
    rmFilter.length > 0,
    !!partnerFilter,
    productFilter.length > 0,
    priceMin != null || priceMax != null,
    !!(createdRange && !isDefaultCreatedRange(createdRange)),
    !!transferRange,
  ].filter(Boolean).length;

  // Build the filter snapshot used by the export engine.
  // Keeps export in sync with whatever is currently visible on screen.
  const buildExportFilterParams = useCallback(
    () => ({
      search: search || undefined,
      status: joinCommaParam(statusFilter) || undefined,
      payment_pending: paymentPendingFilter ? true : undefined,
      rm_uuid: joinCommaParam(rmFilter) || undefined,
      partner_uuid: partnerFilter || undefined,
      product_id: joinCommaParam(productFilter) || undefined,
      altcase_price_min: priceMin ?? undefined,
      altcase_price_max: priceMax ?? undefined,
      date_from: createdRange?.[0]?.startOf('day').toISOString(),
      date_to: createdRange?.[1]?.endOf('day').toISOString(),
      transfer_date_from: transferRange?.[0]?.startOf('day').toISOString(),
      transfer_date_to: transferRange?.[1]?.endOf('day').toISOString(),
      sort_by: sort.field,
      sort_order: sort.order,
    }),
    [search, statusFilter, paymentPendingFilter, rmFilter, partnerFilter, productFilter, priceMin, priceMax, createdRange, transferRange, sort]
  );

  const handleTableChange = (paginationInfo, _filters, sorter) => {
    const nextSort =
      sorter && sorter.order
        ? {
            field: ANT_TO_API_SORT_FIELD[sorter.field] || DEFAULT_SORT.field,
            order: sorter.order === 'ascend' ? 'asc' : 'desc',
          }
        : DEFAULT_SORT;

    const sortChanged = nextSort.field !== sort.field || nextSort.order !== sort.order;
    const pageSizeChanged = paginationInfo.pageSize !== pageSize;

    setPageSize(paginationInfo.pageSize);
    setPage(sortChanged || pageSizeChanged ? 1 : paginationInfo.current);

    if (sortChanged) {
      setSort(nextSort);
    }
  };

  const columns = useMemo(() => {
    const actionsColumn = {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 96,
      render: (_, row) => {
        const st = row.status;
        const showConfirm = canManage && canConfirmPayment(row);
        const showEdit = canShowEditOrderAction(
          st,
          canEditOrderQuantityPermission,
          canEditOrderPartnerPricePermission
        );
        const showUtr = canManage && canUpdateUtr(st);
        const showCancel = canManage && canCancelOrder(st);
        const showTransfer = canManage && canTransferShares(row);
        const transferDisabledHint = canManage && !showTransfer
          ? getTransferSharesDisabledHint(row)
          : null;

        const items = [
          {
            key: 'view',
            label: 'View details',
            onClick: () => openDetailDrawer(row),
          },
          {
            key: 'accounts',
            label: 'Bank & demat accounts',
            onClick: () => {
              setAccountsModalRow(row);
              setAccountsModalOpen(true);
            },
          },
        ];
        if (canManage || showEdit) {
          items.push({ type: 'divider' });
          if (canManage) {
            items.push({
              key: 'confirm',
              label: 'Confirm payment',
              disabled: !showConfirm,
              onClick: () => { if (showConfirm) openConfirm(row); },
            });
          }
          if (showEdit) {
            items.push({
              key: 'edit',
              label: 'Edit order',
              onClick: () => openEdit(row),
            });
          }
          if (canManage) {
            items.push({
              key: 'utr',
              label: 'Update UTR',
              disabled: !showUtr,
              onClick: () => { if (showUtr) openUtr(row); },
            });
            if (row.can_resolve_stock_pending) {
              items.push({
                key: 'resolve-stock',
                label: 'Resolve stock',
                onClick: () => runResolveStockPending(row),
              });
            }
            items.push(
              {
                key: 'transfer',
                label: (
                  <DisabledActionHintLabel
                    title="Mark shares transferred"
                    hint={transferDisabledHint}
                    muted={!showTransfer}
                  />
                ),
                disabled: !showTransfer && !transferDisabledHint,
                onClick: () => { if (showTransfer) openTransfer(row); },
              },
              {
                key: 'cancel',
                label: 'Cancel order',
                danger: true,
                disabled: !showCancel,
                onClick: () => { if (showCancel) openCancel(row); },
              }
            );
          }
        }

        if (row.can_duplicate_offer_expired_order) {
          items.push({
            key: 'duplicate',
            label: 'Duplicate Order',
            onClick: () => openDuplicate(row),
          });
        }

        if (canManageReportExclusion) {
          if (row.exclude_from_reports) {
            items.push({
              key: 'restore-reports',
              label: 'Restore to reports',
              icon: <UndoOutlined />,
              onClick: () => confirmRestoreReportExclusion(row),
            });
          } else if (canExcludeOrderFromReports(row)) {
            items.push({
              key: 'exclude-reports',
              label: 'Exclude from reports',
              icon: <EyeInvisibleOutlined />,
              onClick: () => openExcludeFromReports(row),
            });
          }
        }

        return (
          <Dropdown
            menu={{ items, style: { minWidth: 240 } }}
            trigger={['click']}
          >
            <Button type="text" icon={<MoreOutlined />} aria-label="Actions" />
          </Dropdown>
        );
      },
    };

    return [
      ...getOrderColumns({
        canManage,
        onAssigned: stableLoadOrders,
        onUpdated: stableLoadOrders,
        canViewAltcasePrice,
        canViewSupplier,
        showReportExclusionColumn: canManageReportExclusion,
      }),
      actionsColumn,
    ];
  }, [
    canManage,
    canManageReportExclusion,
    canEditOrderQuantityPermission,
    canEditOrderPartnerPricePermission,
    canViewAltcasePrice,
    canViewSupplier,
    stableLoadOrders,
    openDetailDrawer,
    openConfirm,
    openEdit,
    openUtr,
    openTransfer,
    openCancel,
    openDuplicate,
    openExcludeFromReports,
    confirmRestoreReportExclusion,
    runResolveStockPending,
  ]);

  const statusLogCols = [
    { title: 'From', dataIndex: 'from_status', key: 'from', width: 140 },
    { title: 'To', dataIndex: 'to_status', key: 'to', width: 140 },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', ellipsis: true },
    { title: 'At', dataIndex: 'created_at', key: 'at', width: 160, render: formatDateTime },
    { title: 'By', dataIndex: 'changed_by_name', key: 'by', width: 120 },
  ];

  const fakeCols = [
    { title: 'Qty held', dataIndex: 'qty_held', key: 'q' },
    { title: 'Status', dataIndex: 'status', key: 's' },
    { title: 'Released', dataIndex: 'released_at', key: 'r', render: formatDateTime },
    { title: 'Lot', dataIndex: 'fake_lot_uuid', key: 'lot', ellipsis: true },
  ];

  const realCols = [
    { title: 'Qty used', dataIndex: 'qty_used', key: 'q' },
    { title: 'Fulfilled', dataIndex: 'fulfilled_at', key: 'f', render: formatDateTime },
    { title: 'Lot', dataIndex: 'real_lot_uuid', key: 'lot', ellipsis: true },
    { title: 'Supplier', dataIndex: 'supplier_name', key: 'sup' },
  ];

  const ordersTable = (
    <Table
      rowKey={(row) => row.uuid || row.id}
      columns={columns}
      dataSource={orders}
      loading={loading}
      size="small"
      className="order-mgmt-table [&_.ant-table-thead>tr>th]:whitespace-nowrap [&_.ant-table-tbody>tr>td]:whitespace-nowrap"
      scroll={{ x: 'max-content' }}
      sticky
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        showTotal: (count, range) => (
          <span className="text-xs text-gray-500">
            {range[0]}–{range[1]} of {count}
          </span>
        ),
      }}
      onChange={handleTableChange}
    />
  );

  return (
    <DashboardPageWrapper>
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-white shadow-sm">

        {/* ─── Sticky toolbar ─── */}
        <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-4 py-2.5">
          <PermissionGate permission={PERMISSIONS.INVESTORS_PLACE_SELL_ORDER}>
            <Tooltip title="Place order">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/pre-ipo/order-management/place')}
                className="aspect-square px-0 sm:aspect-auto sm:px-4"
                aria-label="Place order"
              >
                <span className="hidden sm:inline">Place order</span>
              </Button>
            </Tooltip>
          </PermissionGate>
          <PermissionGate permission={PERMISSIONS.RECONCILE_ORDER}>
            <Tooltip title="Reconcile share transfers">
              <Button
                icon={<SwapOutlined />}
                onClick={() => setReconcileDrawerOpen(true)}
                className="aspect-square px-0 sm:aspect-auto sm:px-4"
                aria-label="Reconcile"
              >
                <span className="hidden sm:inline">Reconcile</span>
              </Button>
            </Tooltip>
          </PermissionGate>
          <PermissionGate
            permissions={[
              PERMISSIONS.INVESTORS_PLACE_SELL_ORDER,
              PERMISSIONS.RECONCILE_ORDER,
            ]}
          >
            <div className="mx-1 h-5 w-px shrink-0 bg-gray-200" />
          </PermissionGate>

          <Input
            allowClear
            prefix={<SearchOutlined className="text-gray-400" />}
            placeholder="Search order #, UTR, investor"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={(e) => handleSearch(e.target.value)}
            className="min-w-0 flex-1"
          />

          <Badge count={filterDrawerCount} size="small" offset={[-2, 2]}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFilterDrawerOpen(true)}
              className={`aspect-square px-0 sm:aspect-auto sm:px-4 ${filterDrawerCount > 0 ? 'border-blue-400 text-blue-600' : ''}`}
              aria-label="Filters"
            >
              <span className="hidden sm:inline">Filters</span>
            </Button>
          </Badge>

          <PermissionGate permission={PERMISSIONS.EXPORT_ORDER_DATA}>
            <Button
              icon={<DownloadOutlined />}
              disabled={exportOpen}
              onClick={() => setExportOpen(true)}
              className="aspect-square px-0 sm:aspect-auto sm:px-4"
              aria-label="Export"
            >
              <span className="hidden sm:inline">Export</span>
            </Button>
          </PermissionGate>

          {isSuperAdmin ? (
            <Tooltip title="Payment Receivable — shares transferred, payment pending">
              <Button
                icon={<FiShare2 />}
                onClick={() => setPaymentReceivableOpen(true)}
                className="aspect-square px-0 sm:aspect-auto sm:px-4"
                aria-label="Payment Receivable"
              >
                <span className="hidden sm:inline">Payment Receivable</span>
              </Button>
            </Tooltip>
          ) : null}

          <span className="hidden sm:inline-flex">
            <Tooltip title="Refresh">
              <Button
                icon={<FiRefreshCw />}
                onClick={loadOrders}
                loading={loading}
                aria-label="Refresh"
                className="aspect-square min-w-9 px-0"
              />
            </Tooltip>
          </span>
        </div>

        {/* ─── Active filter chips ─── */}
        {hasActiveFilters ? (
          <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-gray-100 bg-gray-50/60 px-4 py-2">
            {search ? (
              <Tag
                closable
                onClose={() => { setSearch(''); setPage(1); }}
                className="m-0 text-xs"
              >
                Search: <span className="font-medium">{search}</span>
              </Tag>
            ) : null}
            {statusFilter.length ? (
              <Tag
                closable
                onClose={() => handleStatusChange([])}
                className="m-0 text-xs"
              >
                Status:{' '}
                <span className="font-medium">
                  {formatMultiFilterLabels(statusFilter, STATUS_FILTER_OPTIONS, (value) => STATUS_LABEL[value] || value)}
                </span>
              </Tag>
            ) : null}
            {paymentPendingFilter ? (
              <Tag
                closable
                onClose={() => handlePaymentPendingChange(false)}
                className="m-0 text-xs"
              >
                <span className="font-medium">Payment pending after transfer</span>
              </Tag>
            ) : null}
            {rmFilter.length ? (
              <Tag
                closable
                onClose={() => handleRmChange([])}
                className="m-0 text-xs"
              >
                RM:{' '}
                <span className="font-medium">
                  {formatMultiFilterLabels(rmFilter, rmOptions)}
                </span>
              </Tag>
            ) : null}
            {partnerFilter ? (
              <Tag
                closable
                onClose={() => handlePartnerChange(null)}
                className="m-0 text-xs"
              >
                Partner: <span className="font-medium">{selectedPartnerRecord?.name || partnerOptions.find((o) => o.value === partnerFilter)?.label || partnerFilter}</span>
              </Tag>
            ) : null}
            {productFilter.length ? (
              <Tag
                closable
                onClose={() => handleProductChange([])}
                className="m-0 text-xs"
              >
                Product:{' '}
                <span className="font-medium">
                  {formatMultiFilterLabels(productFilter, productOptions)}
                </span>
              </Tag>
            ) : null}
            {(priceMin != null || priceMax != null) ? (
              <Tag
                closable
                onClose={() => { handlePriceMinChange(null); handlePriceMaxChange(null); }}
                className="m-0 text-xs"
              >
                Price: <span className="font-medium">{priceMin != null ? `₹${priceMin}` : '—'} – {priceMax != null ? `₹${priceMax}` : '—'}</span>
              </Tag>
            ) : null}
            {createdRange && !isDefaultCreatedRange(createdRange) ? (
              <Tag
                closable
                onClose={() => handleCreatedRangeChange(null)}
                className="m-0 text-xs"
              >
                Created: <span className="font-medium">{createdRange[0].format('DD MMM')} – {createdRange[1].format('DD MMM')}</span>
              </Tag>
            ) : null}
            {transferRange ? (
              <Tag
                closable
                onClose={() => handleTransferRangeChange(null)}
                className="m-0 text-xs"
              >
                Transfer: <span className="font-medium">{transferRange[0].format('DD MMM')} – {transferRange[1].format('DD MMM')}</span>
              </Tag>
            ) : null}
            <button
              type="button"
              onClick={handleReset}
              className="ml-1 text-xs font-medium text-gray-400 transition-colors hover:text-gray-700"
            >
              Reset all
            </button>
          </div>
        ) : null}

        {/* ─── Table ─── */}
        <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
          {ordersTable}
        </div>
      </div>

      {/* ─── Filter Drawer ─── */}
      <Drawer
        title="Filters"
        placement="right"
        width={360}
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              onClick={() => {
                handleReset();
                setFilterDrawerOpen(false);
              }}
            >
              Reset
            </Button>
            <Button type="primary" onClick={() => setFilterDrawerOpen(false)}>
              Apply
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-5">
          <FilterField label="Created date">
            <AppRangePicker
              value={createdRange}
              onChange={handleCreatedRangeChange}
              format="DD MMM YYYY"
              placeholder={['Start', 'End']}
              className="w-full"
            />
          </FilterField>

          <FilterField label="Transfer date">
            <AppRangePicker
              value={transferRange}
              onChange={handleTransferRangeChange}
              format="DD MMM YYYY"
              placeholder={['Start', 'End']}
              className="w-full"
            />
          </FilterField>

          <FilterField label="Status">
            <Select
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              placeholder="All statuses"
              value={statusFilter}
              onChange={handleStatusChange}
              options={STATUS_FILTER_OPTIONS}
              className="w-full"
            />
          </FilterField>

          <FilterField label="Payment pending after transfer">
            <div className="flex flex-col gap-1.5">
              <Switch
                className="w-fit shrink-0"
                checked={paymentPendingFilter}
                onChange={handlePaymentPendingChange}
              />
              <p className="m-0 text-xs text-gray-500">
                Trusted partner orders: shares transferred, payment not confirmed
              </p>
            </div>
          </FilterField>

          <FilterField label="Partner">
            <Select
              allowClear
              showSearch
              autoClearSearchValue={false}
              loading={loadingPartners}
              placeholder="All partners"
              value={partnerFilter}
              onChange={handlePartnerChange}
              optionFilterProp="label"
              filterOption={false}
              searchValue={partnerSearchValue}
              onSearch={handlePartnerSearch}
              onPopupScroll={handlePartnerPopupScroll}
              onOpenChange={handlePartnerOpenChange}
              dropdownRender={partnerDropdownRender}
              options={partnerOptions}
              className="w-full"
              labelRender={() => {
                const label =
                  selectedPartnerRecord?.name ||
                  selectedPartnerRecord?.email ||
                  partnerOptions.find((option) => String(option.value) === String(partnerFilter))?.label;
                return label ? <span>{label}</span> : null;
              }}
            />
          </FilterField>

          <FilterField label="Product">
            <Select
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              showSearch
              autoClearSearchValue={false}
              loading={loadingProducts}
              placeholder="All products"
              value={productFilter}
              onChange={handleProductChange}
              optionFilterProp="label"
              filterOption={false}
              searchValue={productSearchValue}
              onSearch={handleProductSearch}
              onPopupScroll={handleProductPopupScroll}
              onOpenChange={handleProductOpenChange}
              dropdownRender={productDropdownRender}
              options={productOptions}
              className="w-full"
            />
          </FilterField>

          <FilterField label="RM">
            <Select
              mode="multiple"
              allowClear
              maxTagCount="responsive"
              showSearch
              loading={loadingRms}
              placeholder="All RMs"
              value={rmFilter}
              onChange={handleRmChange}
              optionFilterProp="label"
              options={rmOptions}
              className="w-full"
            />
          </FilterField>

          <FilterField label="Altcase price">
            <div className="flex items-center overflow-hidden rounded-lg border border-gray-200 bg-white">
              <InputNumber
                placeholder="Min"
                min={0}
                precision={2}
                step={1}
                bordered={false}
                controls={false}
                value={priceMin}
                onChange={handlePriceMinChange}
                className="!min-w-0 !flex-1 !rounded-none !shadow-none"
              />
              <span className="shrink-0 px-1 text-gray-300">—</span>
              <InputNumber
                placeholder="Max"
                min={priceMin ?? 0}
                precision={2}
                step={1}
                bordered={false}
                controls={false}
                value={priceMax}
                onChange={handlePriceMaxChange}
                className="!min-w-0 !flex-1 !rounded-none !shadow-none"
              />
            </div>
          </FilterField>
        </div>
      </Drawer>

      <Modal
        open={accountsModalOpen}
        onCancel={() => {
          setAccountsModalOpen(false);
          setAccountsModalRow(null);
        }}
        footer={null}
        destroyOnClose
        width={480}
        centered
        title={null}
        styles={{ body: { paddingTop: 20 } }}
      >
        {accountsModalRow ? (
          <>
            <div className="mb-5">
              <h2 className="text-base font-semibold text-gray-900">Bank &amp; demat accounts</h2>
              <p className="mt-1 text-sm text-gray-500">
                {accountsModalRow.order_number || 'Order'}
                {accountsModalRow.investor_name ? ` · ${accountsModalRow.investor_name}` : ''}
              </p>
            </div>
            <OrderBankDematDetails record={accountsModalRow} />
          </>
        ) : null}
      </Modal>

      <Modal
        title="Confirm payment"
        open={confirmOpen}
        onCancel={() => {
          setConfirmOpen(false);
          setActionTarget(null);
          confirmForm.resetFields();
        }}
        onOk={runConfirmPayment}
        confirmLoading={actionSubmitting}
        destroyOnClose
      >
        <p className="text-sm text-gray-600 mb-3">
          Confirms UTR and moves order to payment received (fake inventory hold). Optional UTR
          if not already set on the order.
        </p>
        <Form form={confirmForm} layout="vertical">
          <Form.Item name="utr_number" label="UTR (optional)">
            <Input placeholder="UTR" maxLength={100} allowClear />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Update UTR"
        open={utrOpen}
        onCancel={() => {
          setUtrOpen(false);
          setActionTarget(null);
          utrForm.resetFields();
        }}
        onOk={runUpdateUtr}
        confirmLoading={actionSubmitting}
        destroyOnClose
      >
        <Form form={utrForm} layout="vertical">
          <Form.Item
            name="utr_number"
            label="UTR"
            rules={[{ required: true, message: 'Enter UTR' }]}
          >
            <Input placeholder="UTR" maxLength={100} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Mark shares transferred"
        open={transferOpen}
        onCancel={() => {
          setTransferOpen(false);
          setActionTarget(null);
          transferForm.resetFields();
        }}
        onOk={runTransferShares}
        confirmLoading={actionSubmitting}
        destroyOnClose
        okText="Transfer shares"
      >
        {actionTarget ? (
          <div className="mb-4 block text-sm text-gray-600 space-y-1">
            <p>
              <span className="font-medium text-gray-800">Order:</span>{' '}
              {actionTarget.order_number}
            </p>
            <p>
              <span className="font-medium text-gray-800">Product:</span>{' '}
              {actionTarget.product_name}
            </p>
            <p>
              <span className="font-medium text-gray-800">Quantity:</span>{' '}
              {actionTarget.quantity}
            </p>
            <p>
              <span className="font-medium text-gray-800">Altcase price:</span>{' '}
              {formatCurrency(actionTarget.altcase_price)}
            </p>
            {getPendingTransferAllocations(actionTarget).length > 0 ? (
              <div>
                <span className="font-medium text-gray-800">Staged lots:</span>
                <ul className="list-disc pl-5 mt-1 text-xs">
                  {getPendingTransferAllocations(actionTarget).map((a) => (
                    <li key={a.inventory_id}>
                      {a.inventory_id} · qty {a.quantity}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
        <Form form={transferForm} layout="vertical">
          <Form.Item
            name="transfer_date"
            label="Transfer date"
            rules={[{ required: true, message: 'Select transfer date' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks (optional)">
            <TextArea rows={3} maxLength={500} placeholder="Transfer notes" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit order"
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setActionTarget(null);
          editForm.resetFields();
        }}
        onOk={runEditOrder}
        confirmLoading={actionSubmitting}
        destroyOnClose
        okText="Save changes"
      >
        {actionTarget ? (
          <p className="mb-3 text-sm text-gray-600">
            {(() => {
              const quantityEditable =
                canEditOrderQuantityPermission && canEditOrderQuantity(actionTarget.status);
              const partnerPriceEditable =
                canEditOrderPartnerPricePermission &&
                canEditOrderPartnerPrice(actionTarget.status);
              const fields = [
                quantityEditable ? 'quantity' : null,
                partnerPriceEditable ? 'partner price' : null,
              ].filter(Boolean);
              return (
                <>
                  Update {fields.join(' and ')} for{' '}
                  <span className="font-medium text-gray-800">{actionTarget.order_number}</span>
                  {actionTarget.product_name ? ` · ${actionTarget.product_name}` : ''}.
                </>
              );
            })()}
          </p>
        ) : null}
        <Form form={editForm} layout="vertical">
          {actionTarget &&
          canEditOrderQuantityPermission &&
          canEditOrderQuantity(actionTarget.status) ? (
            <Form.Item
              name="quantity"
              label="Quantity"
              required={false}
              rules={[
                {
                  validator: (_, value) => {
                    if (value == null || value === '') {
                      return Promise.reject('Enter quantity');
                    }
                    const n = Number(value);
                    if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) {
                      return Promise.reject('Enter a whole number greater than 0');
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                className="w-full"
                min={1}
                step={1}
                precision={0}
                onChange={handleEditQuantityChange}
              />
            </Form.Item>
          ) : null}
          {actionTarget &&
          canEditOrderPartnerPricePermission &&
          canEditOrderPartnerPrice(actionTarget.status) ? (
            <Form.Item
              name="partner_price"
              label="Partner price"
              required={false}
              extra={
                editPricingLoading ? (
                  <span className="text-xs text-gray-500">Updating price from slab…</span>
                ) : null
              }
              rules={[
                {
                  validator: (_, value) => {
                    if (value == null || value === '') {
                      return Promise.reject('Enter partner price');
                    }
                    const n = Number(value);
                    if (!Number.isFinite(n) || n <= 0) {
                      return Promise.reject('Enter a price greater than 0');
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                className="w-full"
                prefix={<span className="text-gray-400">₹</span>}
                min={0.01}
                step={0.01}
                precision={2}
              />
            </Form.Item>
          ) : null}
        </Form>
      </Modal>

      <Modal
        title="Exclude from reports and dashboard"
        open={excludeReportsOpen}
        okText="Exclude from reports"
        okButtonProps={{ danger: true }}
        onCancel={() => {
          setExcludeReportsOpen(false);
          setActionTarget(null);
          excludeReportsForm.resetFields();
        }}
        onOk={runExcludeFromReports}
        confirmLoading={actionSubmitting}
        destroyOnClose
      >
        <div className="flex flex-col gap-4">
          <Alert
            type="warning"
            showIcon
            message="This does not cancel the order or change inventory."
            description={
              <>
                Revenue from this order will be removed from the admin dashboard, MTD / sales /
                leadership reports, and related exports. Team-scoped RMs will no longer see this
                order in their list. Use cancel only if the order should be reversed.
              </>
            }
          />
          <Form form={excludeReportsForm} layout="vertical">
            <Form.Item
              name="reason"
              label="Reason"
              rules={[
                { required: true, message: 'Enter why this order must not count in reports.' },
                { whitespace: true, message: 'Enter why this order must not count in reports.' },
                { max: 500, message: 'Reason must be at most 500 characters.' },
              ]}
            >
              <TextArea
                rows={4}
                maxLength={500}
                showCount
                placeholder="Why this order must not count in reports"
              />
            </Form.Item>
          </Form>
        </div>
      </Modal>

      <Modal
        title="Cancel order"
        open={cancelOpen}
        okText="Cancel order"
        okButtonProps={{ danger: true }}
        onCancel={() => {
          setCancelOpen(false);
          setActionTarget(null);
          cancelForm.resetFields();
        }}
        onOk={runCancel}
        confirmLoading={actionSubmitting}
        destroyOnClose
      >
        <Form form={cancelForm} layout="vertical">
          <Form.Item
            name="remarks"
            label={
              actionTarget && isCancelRemarksRequired(actionTarget.status)
                ? 'Remarks'
                : 'Remarks (optional)'
            }
            rules={
              actionTarget && isCancelRemarksRequired(actionTarget.status)
                ? [
                    { required: true, message: 'Please enter a reason for cancellation' },
                    { whitespace: true, message: 'Please enter a reason for cancellation' },
                  ]
                : []
            }
          >
            <TextArea rows={3} maxLength={500} placeholder="Reason for cancellation" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Duplicate Order"
        open={duplicateOpen}
        okText="Confirm Duplicate"
        onCancel={() => {
          setDuplicateOpen(false);
          setActionTarget(null);
        }}
        onOk={runDuplicateOfferExpiredOrder}
        confirmLoading={actionSubmitting}
        destroyOnClose
      >
        <p className="text-gray-700">
          Duplicate this expired order? A new order will be created with the same partner,
          investor, quantity, and prices. The original expired order will not be changed.
          No notification emails will be sent.
        </p>
      </Modal>

      <Drawer
        title={drawerOrder ? `Order ${drawerOrder.order_number}` : 'Order details'}
        width={720}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerOrder(null);
          setDrawerOrderId(null);
        }}
        destroyOnClose
        footer={
          drawerOrder && canManageReportExclusion ? (
            <Space>
              {drawerOrder.exclude_from_reports ? (
                <Button
                  icon={<UndoOutlined />}
                  loading={actionSubmitting}
                  onClick={() => confirmRestoreReportExclusion(drawerOrder)}
                >
                  Restore to reports
                </Button>
              ) : canExcludeOrderFromReports(drawerOrder) ? (
                <Button
                  danger
                  icon={<EyeInvisibleOutlined />}
                  onClick={() => openExcludeFromReports(drawerOrder)}
                >
                  Exclude from reports
                </Button>
              ) : null}
            </Space>
          ) : null
        }
      >
        {drawerLoading ? (
          <div className="flex justify-center py-12">
            <Spin size="large" />
          </div>
        ) : drawerOrder ? (
          <div className="flex flex-col gap-6">
            {drawerOrder.exclude_from_reports ? (
              <Alert
                type="warning"
                showIcon
                message="Excluded from reports and dashboard"
                description={
                  <div className="space-y-1">
                    {drawerOrder.report_exclusion_reason ? (
                      <p>
                        <span className="font-medium">Reason: </span>
                        {drawerOrder.report_exclusion_reason}
                      </p>
                    ) : null}
                    {drawerOrder.report_excluded_at ? (
                      <p className="text-sm">
                        Excluded at {formatDateTime(drawerOrder.report_excluded_at)}
                      </p>
                    ) : null}
                  </div>
                }
              />
            ) : null}
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Status">
                <Tag color={resolveOrderStatusColor(drawerOrder)}>
                  {resolveOrderStatusLabel(drawerOrder)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Order #">{drawerOrder.order_number}</Descriptions.Item>
              <Descriptions.Item label="Product">{drawerOrder.product_name}</Descriptions.Item>
              <Descriptions.Item label="Quantity">{drawerOrder.quantity}</Descriptions.Item>
              <Descriptions.Item label="Total payable">
                {formatCurrency(drawerOrder.total_payable)}
              </Descriptions.Item>
              <Descriptions.Item label="UTR">{drawerOrder.utr_number}</Descriptions.Item>
              <Descriptions.Item label="RM price">
                {formatCurrency(drawerOrder.rm_price)}
              </Descriptions.Item>
              <Descriptions.Item label="Investment ref">
                {drawerOrder.investment_reference_number}
              </Descriptions.Item>
              <Descriptions.Item label="Investor">{drawerOrder.investor_name}</Descriptions.Item>
              <Descriptions.Item label="Email">{drawerOrder.investor_email}</Descriptions.Item>
            </Descriptions>

            <div>
              <div className="font-medium text-gray-900 mb-2">Status history</div>
              <Table
                size="small"
                rowKey={(r, i) => `${r.created_at}-${i}`}
                columns={statusLogCols}
                dataSource={drawerOrder.status_log ?? []}
                pagination={false}
                locale={{ emptyText: 'No entries' }}
              />
            </div>

            <div>
              <div className="font-medium text-gray-900 mb-2">Fake inventory tracking</div>
              <Table
                size="small"
                rowKey={(r, i) => r.fake_lot_uuid || String(i)}
                columns={fakeCols}
                dataSource={drawerOrder.fake_tracking ?? []}
                pagination={false}
                locale={{ emptyText: 'None' }}
              />
            </div>

            <div>
              <div className="font-medium text-gray-900 mb-2">Real inventory tracking</div>
              <Table
                size="small"
                rowKey={(r, i) => r.real_lot_uuid || String(i)}
                columns={realCols}
                dataSource={drawerOrder.real_tracking ?? []}
                pagination={false}
                locale={{ emptyText: 'None' }}
              />
            </div>
          </div>
        ) : null}
      </Drawer>

      <ShareTransferReconcileDrawer
        open={reconcileDrawerOpen}
        onClose={() => setReconcileDrawerOpen(false)}
        onSuccess={loadOrders}
      />

      <ExcelExportOverlay
        open={exportOpen}
        exportConfig={{
          filename: 'orders',
          sheetName: 'Orders',
          columns: orderExportColumns,
          mapRow: mapOrderExportRow,
          filterParams: buildExportFilterParams(),
          fetchPage: async (filterParams, page, pageSize) => {
            return orderService.adminListOrders({
              ...filterParams,
              page,
              limit: pageSize,
            });
          },
        }}
        onClose={() => setExportOpen(false)}
      />

      <PaymentReceivableSummaryModal
        open={paymentReceivableOpen}
        onClose={() => setPaymentReceivableOpen(false)}
      />
    </DashboardPageWrapper>
  );
}
